<template>
  <div :class="['relative flex flex-col overflow-hidden']">
    <!-- Nav Bar -->
    <Navbar v-if="teamName" />
    <!-- Side Nav Bar -->
    <aside class="fixed mt-[62px] top-0 left-0 h-full hidden lg:w-64 lg:block border-r border-app-gray-600 bg-app-gray-900" aria-label="Sidenav">
      <SideNavbar v-if="teamName" :is-mobile-drawer="false" :team-name="teamName" :show-create-project="showCreateProject" />
    </aside>
    <!-- Body -->
    <main class="lg:ml-64 mt-[62px] bg-app-gray-900 min-h-screen text-app-gray-100">
      <div>
        <Alerts
          v-if="isOverdueInvoiceGated"
          variant="danger"
          :classes="{ heading: 'max-sm:text-sm max-sm:font-semibold', message: 'max-sm:text-xs' }"
          show-icon
          class="mt-4 mx-4"
          heading="Service Suspension Notice"
        >
          <template #alertMessage>
            <div class="text-sm my-2">
              Your service has been suspended due to unpaid overdue invoices. Please attach a payment card and make a payment to resume the service.
            </div>
            <Button v-if="!isBillingPage" size="xs" class="!bg-app-red-700" @click="goToBilling">
              <span>Go to Billing</span>
              <Icon name="arrow-narrow-right-solid-icon" class="ml-2" />
            </Button>
          </template>
        </Alerts>

        <router-view v-if="selectedTeam.id" />
      </div>
    </main>
    <!-- Drawer -->
    <div class="block lg:hidden">
      <Drawer :open="isDrawerOpen" :backdrop-close-icon="false" @on-close="closeMobileDrawer">
        <template #sidebar>
          <SideNavbar v-if="teamName" :is-mobile-drawer="true" :team-name="teamName" :show-create-project="showCreateProject" />
        </template>
      </Drawer>
    </div>

    <div v-if="teamName">
      <UpdateOrganization v-if="needOrganizationUpdate" :selected-team="selectedTeam" />
      <MandatePaymentMethod v-else-if="hasNoPaymentMethodButHasMainnetProjects" :selected-team="selectedTeam" />
    </div>

    <CreateProject v-if="teamName" :is-create-project="isCreateProject" :team="selectedTeam" @closeDialog="isCreateProject = false" />
    <!-- Toaster -->
    <Toaster />
    <!-- Global downloads overlay -->
    <DownloadsOverlay />
  </div>
</template>

<script lang="ts">
import { Icon } from "@toruslabs/vue-components/Icon";
import { defineComponent } from "vue";

import { Alerts } from "@/components/Alerts";
import { Button } from "@/components/Button";
import { Drawer } from "@/components/Drawer";
import useGetHomeRoute from "@/composables/useGetHomeRoute";
import DownloadsOverlay from "@/containers/DownloadsOverlay";
import Navbar from "@/containers/Home/Navbar";
import SideNavbar from "@/containers/Home/SideNavbar";
import CreateProject from "@/containers/Projects/CreateProject";
import UpdateOrganization from "@/containers/Team/UpdateOrganization";
import Toaster from "@/containers/Toaster";
import { ANALYTICS_USER_TYPES, orgAnalytics, projectAnalytics } from "@/helpers/analytics";
import log from "@/helpers/log";
import { billingModule, downloadsModule, projectModule, teamModule, userModule, verifierModule } from "@/store";
import { LOCAL_STORAGE, MAINNET_NETWORKS, ROUTES, SUBSCRIPTION_PLAN, TEAM_PERMISSION } from "@/utils/enums";
import { type Team } from "@/utils/interface";
import { hasPermission } from "@/utils/team";

import { ensureStripe } from "../../../lib/stripeClient";
import MandatePaymentMethod from "./MandatePaymentMethod.vue";

export default defineComponent({
  name: "Home",
  components: {
    Alerts,
    Button,
    Drawer,
    Icon,
    CreateProject,
    Navbar,
    SideNavbar,
    Toaster,
    UpdateOrganization,
    MandatePaymentMethod,
    DownloadsOverlay,
  },
  data: () => ({
    isCreateProject: false,
  }),
  computed: {
    isDrawerOpen(): boolean {
      return userModule.showMobileDrawer;
    },
    teamName(): string {
      return this.$route.params.teamName as string;
    },
    selectedTeam() {
      return teamModule.selectedTeamGetter(this.teamName);
    },
    hasBillingPermission() {
      return hasPermission(this.selectedTeam.role, TEAM_PERMISSION.billing);
    },
    needOrganizationUpdate() {
      return this.selectedTeam.role === TEAM_PERMISSION.owner && (!this.selectedTeam.industry || !this.selectedTeam.organization_size);
    },
    hasNoPaymentMethodButHasMainnetProjects() {
      if (!billingModule.getSubscriptionLoaded(this.selectedTeam.id)) return false;
      const subscription = billingModule.getSubscription(this.selectedTeam.id);
      // for plans above base they would've set up a card or set up bank transfer with us
      if (subscription.customer.plan !== SUBSCRIPTION_PLAN.base) return false;
      // for base plan check if they have a card or not
      if (subscription.card) return false;

      const projects = projectModule.projectsGetter(this.selectedTeam.id) || [];
      const mainnetNetworks = MAINNET_NETWORKS.map((network) => network.value);
      const hasMainnetProject = projects.some((project) => mainnetNetworks.includes(project.network));
      return hasMainnetProject;
    },
    isOverdueInvoiceGated() {
      if (this.selectedTeam.invoice_gate_bypassed) return false;
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      return (
        billingModule.getPendingInvoices(this.selectedTeam.id).filter((invoice) => invoice.due_date && new Date(invoice.due_date) <= threeMonthsAgo)
          .length > 0
      );
    },
    isBillingPage() {
      return this.$route.name === ROUTES.TEAM_BILLING;
    },
  },
  watch: {
    // this is for subsequent navigations to organization home route e.g. (via router.push or router.replace)
    // e.g. by navigating to /organization or /organization/some-team-name
    // the selectedTeam watcher is not triggered in that case since the team hasn't changed
    // but we still need to navigate user to the correct organization base route
    "$route.name": {
      async handler(newRoute) {
        // when reaching home route we should redirect to an organisation (if available) and the base home route
        // this is to ensure that we are always on a valid route
        if (newRoute === ROUTES.HOME) {
          this.redirectToOrganization();
        }
      },
    },
    selectedTeam: {
      async handler(newSelectedTeam: Team, prevSelectedTeam: Team | undefined) {
        // ensure teams loaded
        if (teamModule.teamsGetter.length === 0) return;

        if (!newSelectedTeam.id) {
          this.redirectToOrganization();
          return;
        }

        if (newSelectedTeam.id && newSelectedTeam.id !== prevSelectedTeam?.id) {
          // analytics
          orgAnalytics.identify(newSelectedTeam.id.toString(), {
            name: newSelectedTeam.name,
            team_id: newSelectedTeam.id,
            industry: newSelectedTeam.industry,
            organization_size: newSelectedTeam.organization_size,
            organization_url: newSelectedTeam.organization_url,
            type: ANALYTICS_USER_TYPES.ORGANIZATION,
          });
          const globalProperties = {
            team_id: newSelectedTeam.id,
            user_id: userModule.userProfileGetter?.ID,
            user_auth_connection: userModule.userInfoGetter?.authConnection,
            user_type_of_login: userModule.typeOfLogin,
            user_mfa_enabled: false,
            user_role: newSelectedTeam.role,
          };
          orgAnalytics.setGlobalProperties(globalProperties);
          projectAnalytics.setGlobalProperties(globalProperties);
          orgAnalytics.group(newSelectedTeam.id.toString(), {
            name: newSelectedTeam.name,
            team_id: newSelectedTeam.id,
            team_size: newSelectedTeam.organization_size,
            team_url: newSelectedTeam.organization_url,
            team_industry: newSelectedTeam.industry,
          });

          this.initData();
          this.setLastVisitedOrganization();
          if (this.$route.name === ROUTES.HOME) {
            this.redirectToOrganization();
          }
        }
      },
      immediate: true,
    },
  },
  created() {
    // Warm Stripe.js once for the whole authenticated dashboard. Every Stripe consumer
    // (billing, add/update card dialogs, plan changes) is a descendant of this view, so this
    // single call replaces per-component loads. Lightweight routes (agentic, login) never mount
    // Home, so Stripe stays off their critical path.
    void ensureStripe();
    // Rehydrate any in-flight/completed user exports so the downloads overlay
    // survives navigation and full page reloads. Pass the authenticated userId
    // so the store reads from the correct user-scoped localStorage key.
    downloadsModule.hydrate(userModule.userId ?? "");
  },
  methods: {
    redirectToOrganization() {
      const lastVisitedOrganization = teamModule.teamsGetter.find(
        (team) => team.name === localStorage.getItem(LOCAL_STORAGE.lastVisitedOrganization)
      );
      const params = { teamName: lastVisitedOrganization?.name ?? teamModule.teamsGetter[0].name };
      this.$router.replace({ name: useGetHomeRoute(), params, query: this.$route.query });
    },
    setLastVisitedOrganization() {
      localStorage.setItem(LOCAL_STORAGE.lastVisitedOrganization, this.teamName ?? "");
    },
    async initData() {
      try {
        await Promise.all([
          projectModule.getProjects({ teamId: this.selectedTeam.id }),
          projectModule.fetchChainsListFromRegistry(),
          verifierModule.getLegacyVerifiers({ teamId: this.selectedTeam.id }),
          billingModule.fetchSubscription({ teamId: this.selectedTeam.id }),
          this.hasBillingPermission ? billingModule.getInvoices({ teamId: this.selectedTeam.id }) : undefined,
        ]);
      } catch (e) {
        log.error("Failed to init data", e);
      }
    },
    closeMobileDrawer(): void {
      userModule.closeMobileDrawer();
    },
    showCreateProject() {
      this.isCreateProject = true;
    },
    goToBilling() {
      this.$router.push(`/organization/${this.teamName}/billing#payment`);
    },
  },
});
</script>
